import type { ReactElement } from 'react';

import { Checkbox } from 'cpool-components/input/Checkbox';
import { NumberInput } from 'cpool-components/input/NumberInput';
import { TextInput } from 'cpool-components/input/TextInput';
import { PARKING_TYPES, RENOVATION_TYPES } from 'cpool-constants';
import type { Schema_Baubeschreibung } from 'cpool-schemas';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OptionGroupFieldset } from './FormControls';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema';

type ListFieldProps = {
    disabled?: boolean;
    realEstateIndex: number;
};

type ParkingSpace = Schema_Baubeschreibung['sanitaerGaragen']['garagenStellplaetze'][number];
type Renovation = Schema_Baubeschreibung['sanierungen']['sanierungen'][number];

const RENOVATION_TYPES_IN_DISPLAY_ORDER = [
    ...Object.values(RENOVATION_TYPES).filter((renovationType) => renovationType !== RENOVATION_TYPES.ANDERE),
    RENOVATION_TYPES.ANDERE,
];

function toggleParkingSpace(
    entries: ParkingSpace[],
    parkingType: ParkingSpace['art'],
    checked: boolean,
): ParkingSpace[] {
    if (checked) return [...entries, { anzahl: 1, art: parkingType }];
    return entries.filter(({ art }) => art !== parkingType);
}

function updateParkingSpaceCount(
    entries: ParkingSpace[],
    parkingType: ParkingSpace['art'],
    count: number | null,
): ParkingSpace[] {
    return entries.map((entry) => {
        if (entry.art !== parkingType) return entry;
        return { ...entry, anzahl: count ?? 1 };
    });
}

export function ParkingSpacesField({ disabled, realEstateIndex }: ListFieldProps): ReactElement {
    const { t } = useTranslation();
    const { control } = useFormContext<SchemaRealEstatesForm>();
    const name = `immobilien.${realEstateIndex}.baubeschreibung.sanitaerGaragen.garagenStellplaetze` as const;
    const countLabel = t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.COUNT');

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => {
                const entries = field.value ?? [];

                return (
                    <OptionGroupFieldset className="py-3" label={t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.PARKING')}>
                        <div className="clear-both grid min-w-0 gap-y-2">
                            {Object.values(PARKING_TYPES).map((parkingType) => {
                                const entry = entries.find(({ art }) => art === parkingType);
                                const parkingLabel = t(
                                    `REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.PARKING.${parkingType}`,
                                );

                                return (
                                    <div
                                        key={parkingType}
                                        className="grid h-8 min-w-0 grid-cols-1 items-center gap-x-8 sm:grid-cols-2"
                                    >
                                        <Checkbox
                                            id={`${name}.${parkingType}`}
                                            label={parkingLabel}
                                            checked={entry !== undefined}
                                            onChange={(event) =>
                                                field.onChange(
                                                    toggleParkingSpace(entries, parkingType, event.target.checked),
                                                )
                                            }
                                            disabled={disabled}
                                        />
                                        {entry && (
                                            <NumberInput
                                                id={`${name}.${parkingType}.anzahl`}
                                                label={countLabel}
                                                value={entry.anzahl}
                                                onChange={(count) =>
                                                    field.onChange(updateParkingSpaceCount(entries, parkingType, count))
                                                }
                                                min={1}
                                                allowDecimals={false}
                                                disabled={disabled}
                                                className="h-8 w-full min-w-0 items-center gap-6 [&>div]:h-8 [&>div]:min-w-0"
                                                labelClassName="h-8 min-h-0 w-11 shrink-0 justify-start py-0"
                                                inputClassName="min-w-0"
                                                aria-label={`${parkingLabel} – ${countLabel}`}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </OptionGroupFieldset>
                );
            }}
        />
    );
}

function createRenovation(renovationType: Renovation['art']): Renovation {
    return {
        art: renovationType,
        bezeichnung: null,
        ergaenzungen: null,
        jahr: null,
    };
}

function toggleRenovation(entries: Renovation[], renovationType: Renovation['art'], checked: boolean): Renovation[] {
    if (checked) return [...entries, createRenovation(renovationType)];
    return entries.filter(({ art }) => art !== renovationType);
}

function updateRenovation(
    entries: Renovation[],
    renovationType: Renovation['art'],
    change: Partial<Renovation>,
): Renovation[] {
    return entries.map((entry) => {
        if (entry.art !== renovationType) return entry;
        return { ...entry, ...change };
    });
}

export function RenovationsField({ disabled, realEstateIndex }: ListFieldProps): ReactElement {
    const { t } = useTranslation();
    const { control } = useFormContext<SchemaRealEstatesForm>();
    const name = `immobilien.${realEstateIndex}.baubeschreibung.sanierungen.sanierungen` as const;
    const designationLabel = t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.DESIGNATION');
    const supplementsLabel = t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.SUPPLEMENTS');
    const yearLabel = t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.RENOVATION_YEAR');

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => {
                const entries = field.value ?? [];

                return (
                    <fieldset className="min-w-0 space-y-2">
                        <legend className="text-sm font-bold text-midnight">
                            {t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.RENOVATIONS')}
                        </legend>
                        {RENOVATION_TYPES_IN_DISPLAY_ORDER.map((renovationType) => {
                            const entry = entries.find(({ art }) => art === renovationType);
                            const renovationLabel = t(
                                `REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.RENOVATIONS.${renovationType}`,
                            );
                            const updateEntry = (change: Partial<Renovation>) =>
                                field.onChange(updateRenovation(entries, renovationType, change));
                            const isOtherRenovation = renovationType === RENOVATION_TYPES.ANDERE;

                            return (
                                <div
                                    key={renovationType}
                                    className="grid min-h-8 min-w-0 grid-cols-1 items-center gap-3 xl:h-8 xl:grid-cols-[minmax(280px,440px)_minmax(0,1fr)]"
                                >
                                    <Checkbox
                                        id={`${name}.${renovationType}`}
                                        label={renovationLabel}
                                        checked={entry !== undefined}
                                        onChange={(event) =>
                                            field.onChange(
                                                toggleRenovation(entries, renovationType, event.target.checked),
                                            )
                                        }
                                        disabled={disabled}
                                    />
                                    {entry && (
                                        <div className="flex h-8 w-full min-w-0 items-center gap-2">
                                            <NumberInput
                                                id={`${name}.${renovationType}.jahr`}
                                                placeholder={yearLabel}
                                                value={entry.jahr}
                                                onChange={(jahr) => updateEntry({ jahr })}
                                                min={1000}
                                                max={9999}
                                                allowDecimals={false}
                                                disabled={disabled}
                                                className="h-8 w-20 min-w-0 shrink-0 [&>div]:h-8 [&>div]:min-w-0"
                                                inputClassName="min-w-0"
                                                aria-label={`${renovationLabel} – ${yearLabel}`}
                                            />
                                            {isOtherRenovation && (
                                                <TextInput
                                                    id={`${name}.${renovationType}.bezeichnung`}
                                                    placeholder={designationLabel}
                                                    value={entry.bezeichnung ?? ''}
                                                    onChange={(event) =>
                                                        updateEntry({
                                                            bezeichnung: event.target.value || null,
                                                        })
                                                    }
                                                    disabled={disabled}
                                                    className="h-8 w-32 min-w-0 shrink-0 [&>div]:h-8 [&>div]:min-w-0"
                                                    inputClassName="min-w-0"
                                                    aria-label={`${renovationLabel} – ${designationLabel}`}
                                                />
                                            )}
                                            <TextInput
                                                id={`${name}.${renovationType}.ergaenzungen`}
                                                placeholder={supplementsLabel}
                                                value={entry.ergaenzungen ?? ''}
                                                onChange={(event) =>
                                                    updateEntry({
                                                        ergaenzungen: event.target.value || null,
                                                    })
                                                }
                                                disabled={disabled}
                                                className="h-8 min-w-32 flex-1 xl:min-w-0 [&>div]:h-8 [&>div]:min-w-0"
                                                inputClassName="min-w-0"
                                                aria-label={`${renovationLabel} – ${supplementsLabel}`}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </fieldset>
                );
            }}
        />
    );
}
